import React, { useEffect, useState } from 'react';
import { Box, TextField, Typography, List, ListItem, ListItemText, CircularProgress, Pagination, Divider, Stack, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import { get } from '../utils/api';
import AddDevoteeDialog from '../components/AddDevoteeDialog';

const DevoteeSearchPage = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Debounced search effect
  useEffect(() => {
    let cancelled = false;
    const handler = setTimeout(async () => {
      // Auto-search only when query has at least 3 chars
      const q = query.trim();
      if (q.length < 3) {
        setResults([]);
        setTotalPages(1);
        setTotalItems(0);
        setPage(1);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const data = await get('/ldb/customers', { q, page, per_page: perPage });
        const items = Array.isArray(data?.data) ? data.data : [];
        setResults(items);
        const meta = data?.meta || {};
        // /ldb returns { total } not total_pages; compute pages
        const total = Number(meta.total || items.length);
        setTotalItems(total);
        setTotalPages(Math.max(1, Math.ceil(total / perPage)));
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to search users');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);




  return () => {
      cancelled = true;
      clearTimeout(handler);
    };
  }, [query, page, perPage]);

  const onChangeQuery = (e) => {
    setQuery(e.target.value);
    setPage(1); // reset to first page on new query
  };

  const formatName = (u) => {
    const first = u.first_name || u.billing?.first_name || '';
    const last = u.last_name || u.billing?.last_name || '';
    const name = `${first} ${last}`.trim();
    return name || '(No name)';
  };

  const formatEmail = (u) => u.email || u.billing?.email || '';

  const formatPhone = (u) => (u.phone || u.billing?.phone || '').toString();

  const handleAddSuccess = () => {
    // Refresh search results if there's a query
    if (query.trim().length >= 3) {
      // Trigger a refresh by updating the query slightly and back
      const currentQuery = query;
      setQuery('');
      setTimeout(() => setQuery(currentQuery), 100);
    }
  };


  const handlePostPayment = (devotee) => {
    setAddDialogOpen(false);
    const cid = devotee?.customer_id || devotee?.id || devotee?.user_id || devotee?.ID;
    if (cid) {
      navigate(`/post-payment?customer_id=${encodeURIComponent(cid)}`, { state: { user: devotee } });
      return;
    }
    navigate('/post-payment', { state: { user: devotee } });
  };

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Search Devotee</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add New Devotee
        </Button>
      </Box>

      <TextField
        fullWidth
        placeholder="Search by first, last or email (cached, min 3 chars)"
        value={query}
        onChange={onChangeQuery}
        autoFocus
        size="small"
      />

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {query.trim().length < 3 ? 'Type at least 3 characters to search' : `Results: ${totalItems}`}
        </Typography>
      </Stack>

      <Divider sx={{ my: 2 }} />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {!loading && query.trim().length >= 3 && results.length === 0 && !error && (
        <Typography variant="body2" color="text.secondary">
          No devotees found matching your search.
        </Typography>
      )}

      {!loading && results.length > 0 && (
        <>
          <List dense>
            {results.map((u) => (
              <ListItem
                key={u.id || `${formatEmail(u)}-${formatPhone(u)}`}
                divider
                secondaryAction={
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate(`/devotees/${u.id || 'devotee-details'}`, { state: { user: u } })}
                  >
                    Select
                  </Button>
                }
                onClick={() => navigate(`/devotees/${u.id || 'devotee-details'}`, { state: { user: u } })}
                sx={{ '&:hover': { backgroundColor: 'action.hover' }, cursor: 'pointer' }}
              >
                <ListItemText
                  primary={formatName(u)}
                  secondary={[
                    formatEmail(u) || null,
                    formatPhone(u) || null,
                  ].filter(Boolean).join(' • ')}
                />
              </ListItem>
            ))}
          </List>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      <AddDevoteeDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={handleAddSuccess}
        onPostPayment={handlePostPayment}
      />
    </PageLayout>
  );
};

export default DevoteeSearchPage;
